import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Plus, Trash2, Star, Building2, Wallet,
  Clock, Calendar, ArrowDownToLine, Settings2, Check
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const methodTypes = [
  { value: 'bank_transfer', label: 'Transferencia Bancaria', icon: Building2 },
  { value: 'paypal', label: 'PayPal', icon: Wallet },
  { value: 'crypto', label: 'Criptomoneda', icon: CreditCard },
  { value: 'mobile_money', label: 'Dinero Movil', icon: CreditCard },
];

const scheduleOptions = [
  { value: 'manual', label: 'Manual', desc: 'Retira cuando quieras' },
  { value: 'daily', label: 'Diario', desc: 'Cada dia automaticamente' },
  { value: 'weekly', label: 'Semanal', desc: 'Cada lunes' },
  { value: 'biweekly', label: 'Quincenal', desc: 'Cada 15 dias' },
  { value: 'monthly', label: 'Mensual', desc: 'Primer dia del mes' },
];

const PaymentMethods = () => {
  const [methods, setMethods] = useState([]);
  const [schedule, setSchedule] = useState({ schedule_type: 'manual', min_amount: 0 });
  const [showAdd, setShowAdd] = useState(false);
  const [balance, setBalance] = useState(null);
  const [form, setForm] = useState({ method_type: 'bank_transfer', name: '', details: {}, is_primary: false });

  const fetchData = useCallback(async () => {
    try {
      const [mRes, sRes, bRes] = await Promise.all([
        axios.get(`${API}/payment-methods`),
        axios.get(`${API}/fund-withdrawal/schedule`),
        axios.get(`${API}/escrow/balance`)
      ]);
      setMethods(mRes.data);
      setSchedule(sRes.data);
      setBalance(bRes.data);
    } catch (err) { console.warn('Fetch error:', err?.message); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error('Nombre requerido'); return; }
    try {
      await axios.post(`${API}/payment-methods`, form);
      toast.success('Metodo de pago agregado');
      setShowAdd(false);
      setForm({ method_type: 'bank_transfer', name: '', details: {}, is_primary: false });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/payment-methods/${id}`);
      toast.success('Eliminado');
      fetchData();
    } catch (err) { toast.error('Error al eliminar'); }
  };

  const handleScheduleUpdate = async (type) => {
    try {
      await axios.put(`${API}/fund-withdrawal/schedule`, { schedule_type: type, min_amount: schedule.min_amount || 0 });
      setSchedule(prev => ({ ...prev, schedule_type: type }));
      toast.success('Frecuencia actualizada');
    } catch (err) { toast.error('Error'); }
  };

  const handleMinAmountUpdate = async (val) => {
    const amount = parseFloat(val) || 0;
    try {
      await axios.put(`${API}/fund-withdrawal/schedule`, { schedule_type: schedule.schedule_type, min_amount: amount });
      setSchedule(prev => ({ ...prev, min_amount: amount }));
    } catch (err) { console.warn('Min amount update error:', err?.message); }
  };

  return (
    <div data-testid="payment-methods-page" className="min-h-screen pb-24">
      <Header title="Metodos de Pago" subtitle="Gestiona donde recibir tus fondos" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-6 mt-4">

        {/* Balance Summary */}
        {balance && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Retenido', value: balance.held_balance, color: 'text-amber-700 dark:text-amber-400' },
              { label: 'Disponible', value: balance.available_to_withdraw, color: 'text-emerald-700 dark:text-emerald-400' },
              { label: 'Liberado', value: balance.released_total, color: 'text-brand-deep dark:text-brand-green' },
              { label: 'Comisiones', value: balance.platform_fees, color: 'text-brand-deep dark:text-brand-deep dark:text-brand-green' },
            ].map(item => (
              <Card key={item.label} className="bg-card/80 border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className={cn("text-xl font-bold", item.color)}>${item.value?.toFixed(2)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Withdrawal Schedule */}
        <Card className="bg-card/80 border-white/10">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
              <h3 className="font-semibold">Frecuencia de Retiro</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
              {scheduleOptions.map(opt => (
                <button key={opt.value} data-testid={`schedule-${opt.value}`}
                  onClick={() => handleScheduleUpdate(opt.value)}
                  className={cn("p-3 rounded-xl border text-center transition-all",
                    schedule.schedule_type === opt.value
                      ? "bg-violet-500/15 border-violet-500/40 ring-1 ring-violet-500/30"
                      : "bg-white/5 border-white/10 hover:bg-white/10")}>
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</p>
                  {schedule.schedule_type === opt.value && <Check className="w-3 h-3 text-brand-deep dark:text-brand-deep dark:text-brand-green mx-auto mt-1" />}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-sm whitespace-nowrap">Monto minimo ($):</Label>
              <Input type="number" value={schedule.min_amount || ''} onChange={e => handleMinAmountUpdate(e.target.value)}
                className="w-32 bg-white/5 border-white/10" placeholder="0" />
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card className="bg-card/80 border-white/10">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
                <h3 className="font-semibold">Metodos de Cobro</h3>
                <Badge variant="outline" className="text-[10px]">{methods.length}</Badge>
              </div>
              <Button size="sm" onClick={() => setShowAdd(true)} className="gap-1 bg-primary">
                <Plus className="w-3.5 h-3.5" /> Agregar
              </Button>
            </div>
            <div className="space-y-2">
              {methods.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Wallet className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No hay metodos de pago configurados</p>
                  <p className="text-xs mt-1">Agrega uno para recibir tus fondos</p>
                </div>
              )}
              {methods.map(m => {
                const typeInfo = methodTypes.find(t => t.value === m.method_type) || methodTypes[0];
                const Icon = typeInfo.icon;
                return (
                  <div key={m.id} data-testid={`method-${m.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-xs text-muted-foreground">{typeInfo.label}</p>
                    </div>
                    {m.is_primary && <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 text-[10px]">Principal</Badge>}
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(m.id)} className="text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Agregar Metodo de Pago</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo</Label>
              <Select value={form.method_type} onValueChange={v => setForm(f => ({ ...f, method_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {methodTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre / Alias</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Mi cuenta principal" />
            </div>
            {form.method_type === 'bank_transfer' && (
              <>
                <div><Label>Banco</Label><Input placeholder="Bancolombia" onChange={e => setForm(f => ({ ...f, details: { ...f.details, bank: e.target.value } }))} /></div>
                <div><Label>Numero de cuenta</Label><Input placeholder="****5678" onChange={e => setForm(f => ({ ...f, details: { ...f.details, account: e.target.value } }))} /></div>
              </>
            )}
            {form.method_type === 'paypal' && (
              <div><Label>Email PayPal</Label><Input placeholder="correo@paypal.com" onChange={e => setForm(f => ({ ...f, details: { ...f.details, email: e.target.value } }))} /></div>
            )}
            {form.method_type === 'crypto' && (
              <>
                <div><Label>Red</Label><Input placeholder="Ethereum, Bitcoin, etc." onChange={e => setForm(f => ({ ...f, details: { ...f.details, network: e.target.value } }))} /></div>
                <div><Label>Direccion Wallet</Label><Input placeholder="0x..." onChange={e => setForm(f => ({ ...f, details: { ...f.details, address: e.target.value } }))} /></div>
              </>
            )}
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_primary} onChange={e => setForm(f => ({ ...f, is_primary: e.target.checked }))} className="rounded" />
              <Label className="text-sm">Establecer como principal</Label>
            </div>
            <Button onClick={handleAdd} className="w-full bg-primary">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethods;
